import React, { useState } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { motion } from 'framer-motion';
import { tokenDistribution, tokenConfig } from '@/constants';

const TokenomicsPieChart: React.FC = () => {
  const [hoveredSegment, setHoveredSegment] = useState<number | null>(null);

  const onPieEnter = (_: any, index: number) => {
    setHoveredSegment(index);
  };

  const onPieLeave = () => {
    setHoveredSegment(null);
  };

  const renderCustomizedLabel = (entry: any) => {
    const RADIAN = Math.PI / 180;
    const radius = 100; // Moved inward from 120 to 100
    const x = entry.cx + radius * Math.cos(-entry.midAngle * RADIAN);
    const y = entry.cy + radius * Math.sin(-entry.midAngle * RADIAN);

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor="middle"
        dominantBaseline="central"
        className="text-sm font-bold drop-shadow-lg"
        style={{
          textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
        }}
      >
        {`${entry.value}%`}
      </text>
    );
  };

  return (
    <div className="w-full">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        className="relative"
      >
        {/* Chart Container */}
        <div className="relative bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 shadow-lg border border-gray-100">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">FSPY Token Distribution</h3>
            <p className="text-gray-600">Total Supply: {tokenConfig.totalSupply} Billion FSPY</p>
          </div>

          <div className="flex flex-col lg:flex-row items-center justify-center gap-8">
            {/* Pie Chart */}
            <div className="relative w-full max-w-[400px] aspect-square">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={tokenDistribution}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderCustomizedLabel}
                    outerRadius={140}
                    innerRadius={60}
                    fill="#8884d8"
                    dataKey="value"
                    onMouseEnter={onPieEnter}
                    onMouseLeave={onPieLeave}
                    stroke="#ffffff"
                    strokeWidth={2}
                  >
                    {tokenDistribution.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={entry.color}
                        style={{
                          filter: hoveredSegment === index ? 'brightness(1.1)' : 'brightness(1)',
                          transform: hoveredSegment === index ? 'scale(1.02)' : 'scale(1)',
                          transition: 'all 0.2s ease-in-out',
                          cursor: 'pointer'
                        }}
                      />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              
              {/* Center Label */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="text-center">
                  <div className="text-3xl font-bold text-gray-900">FSPY</div>
                  <div className="text-sm text-gray-600">Token</div>
                </div>
              </div>
            </div>

            {/* Legend */}
            <div className="space-y-3 max-w-md">
              {tokenDistribution.map((item, index) => (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                  className={`flex items-start gap-3 p-4 rounded-lg transition-all duration-300 cursor-pointer transform ${
                    hoveredSegment === index 
                      ? 'bg-foodsply-green text-white scale-105 shadow-lg' 
                      : 'bg-gray-50 hover:bg-gray-100 hover:scale-102'
                  }`}
                  onMouseEnter={() => setHoveredSegment(index)}
                  onMouseLeave={() => setHoveredSegment(null)}
                >
                  <div 
                    className={`w-4 h-4 rounded-full flex-shrink-0 mt-0.5 shadow-sm transition-all duration-300 ${
                      hoveredSegment === index ? 'ring-2 ring-white ring-opacity-50' : ''
                    }`}
                    style={{ backgroundColor: item.color }}
                  />
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className={`font-semibold text-sm transition-colors duration-300 ${
                        hoveredSegment === index ? 'text-white' : 'text-gray-900'
                      }`}>
                        {item.name}
                      </h4>
                      <span className={`font-bold text-lg transition-colors duration-300 ${
                        hoveredSegment === index ? 'text-white' : 'text-gray-900'
                      }`}>
                        {item.value}%
                      </span>
                    </div>
                    <p className={`text-xs leading-relaxed transition-colors duration-300 ${
                      hoveredSegment === index ? 'text-white text-opacity-90' : 'text-gray-600'
                    }`}>
                      {item.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>


        </div>
      </motion.div>
    </div>
  );
};

export default TokenomicsPieChart;